'use client';

import Box from '@mui/material/Box';
import { useMemo } from 'react';
import Marquee from 'react-fast-marquee';

import { ClientOnly } from '@/components/ClientOnlyMarquee';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { ParsePillHeading } from '@/components/ParsePillHeading';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { shuffle } from '@/lib/ArrayHelpers';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksLenderList,
  Enum_Componentshareddisplayoptions_Contentswidth,
  UploadFile,
} from '@/types';

import { StrapiImage } from './Utility/StrapiImage';

interface LenderListProps {
  block?: ComponentBlocksLenderList;
}

export function LenderList({ block }: LenderListProps) {
  const { siteSettings } = useSiteSettings();

  const numberOfLenders = block?.NumberOfLenders ?? '';
  const blockImages = (block?.LenderLogos ?? []).filter(Boolean) as UploadFile[];
  const siteImages = (siteSettings?.LenderLogos ?? []).filter(Boolean) as UploadFile[];

  const images = blockImages.length > 0 ? blockImages : siteImages;
  const titleText = block?.TitleText ?? '';

  const shuffledImages = useMemo<UploadFile[]>(() => shuffle([...images]), [images]);

  if (images.length === 0) return null;

  const imageSx = {
    display: 'inline-flex',
    alignItems: 'center',
    margin: '0 1rem',
    transition: 'transform 0.3s ease, opacity 0.3s ease, box-shadow 0.3s ease',
    borderRadius: '8px',
    '& img': {
      height: '60px',
      width: 'auto',
      maxWidth: '8rem',
      display: 'block',
    },
    '&:hover': {
      transform: 'scale(1.08) translateY(-2px)',
      opacity: 0.95,
      boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
    },
  };

  return (
    <CustomBackground
      data-component="LenderList"
      backgroundOptions={(block?.LenderListBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...block?.LenderListDisplayOptions,
          ContentsWidth:
            block?.LenderListDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: block?.LenderListDisplayOptions?.PaddingTop ?? 'xs',
          PaddingBottom: block?.LenderListDisplayOptions?.PaddingBottom ?? 'sm',
          PaddingInline: block?.LenderListDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
    >
      <Box>
        {(numberOfLenders || titleText) && (
          <Box
            sx={(theme) => ({
              h2: {
                fontSize: theme.typography.h1.fontSize,
                strong: {
                  fontSize: `calc(${theme.typography.h1.fontSize} * 1.1)`,
                },
              },
              marginBottom: 6,
            })}
          >
            <ParsePillHeading
              title={`${numberOfLenders ? `_${numberOfLenders}_ ` : ''}${titleText}`}
            />
          </Box>
        )}

        <Box
          sx={(theme) => ({
            my: 1,
            ...(block?.LenderListBorderLogos === true
              ? {
                  borderRadius: 50,
                  py: 4,
                  border: `solid 0.75rem ${theme.palette.purple[600]}`,
                  background: theme.palette.common.white,
                  overflow: 'hidden',
                }
              : {}),
          })}
        >
          <ClientOnly
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  gap: 4,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  py: 2,
                }}
              >
                {shuffledImages.slice(0, 8).map((image: UploadFile, index: number) => (
                  <Box px={{ xs: 2, sm: 3, md: 4 }} key={index} sx={{ minWidth: 80 }}>
                    <StrapiImage
                      image={image}
                      size="logo"
                      fixAxis="height"
                      imageHeight={60}
                      sx={imageSx}
                    />
                  </Box>
                ))}
              </Box>
            }
          >
            <Marquee gradient={false} pauseOnHover>
              {shuffledImages.map((image: UploadFile, index: number) => (
                <Box px={{ xs: 2, sm: 3, md: 4 }} key={index} sx={{ minWidth: 80 }}>
                  <StrapiImage
                    image={image}
                    size="logo"
                    fixAxis="height"
                    imageHeight={60}
                    sx={imageSx}
                  />
                </Box>
              ))}
            </Marquee>
          </ClientOnly>
        </Box>
      </Box>
    </CustomBackground>
  );
}
